// src/modules/accounting/pages/StockRegisterPage.jsx
//
// The Stock Register: every metal movement in grams + ₹, linked to its journal
// entry. Automatic rows come from case purchases (IN) and smelter dispatches
// (OUT); manual rows (manual in / write-off / direct sale / adjustment) go
// through the Approval Engine and post only on approval.

import React, { useEffect, useState, useCallback } from 'react';
import {
  Card, Button, Modal, Form, Input, InputNumber, Select, Tag, Space,
  message, Typography, Row, Col, Tooltip, Popconfirm, Alert, Spin,
  Statistic, DatePicker, Radio, Badge
} from 'antd';
import {
  PlusOutlined, ReloadOutlined, ArrowDownOutlined, ArrowUpOutlined,
  ClockCircleOutlined, WalletOutlined, BankOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  fetchStockSummary, fetchStockMovements, createStockMovement, cancelStockMovement,
  fetchFunds,
} from '../api';
import DenominationCountForm from '../../treasury/components/DenominationCountForm';
import DataTable from '../../../components/UI/DataTable';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

// Cash vs bank is decided by the fund's linked GL account, matching
// stockRegisterService.fundProceedsMode on the backend: 10xx = cash, 11xx = bank.
const fundProceedsMode = (code) => {
  const c = String(code || '');
  if (c.startsWith('10')) return 'CASH';
  if (c.startsWith('11')) return 'BANK';
  return null;
};

const NOTE_VALUES = { note2000: 2000, note500: 500, note200: 200, note100: 100, note50: 50, note20: 20, note10: 10, note5: 5, note2: 2, note1: 1 };
const denomTotal = (c = {}) => Object.entries(NOTE_VALUES).reduce((s, [k, v]) => s + (c[k] || 0) * v, 0);

const MOVEMENT_TYPE_META = {
  PURCHASE_IN: { label: 'Purchase (auto)', color: 'green' },
  DISPATCH_OUT: { label: 'Smelter Dispatch (auto)', color: 'volcano' },
  MELT_LOSS: { label: 'Melting Loss (auto)', color: 'orange' },
  MANUAL_IN: { label: 'Manual In', color: 'cyan' },
  WRITE_OFF: { label: 'Write-off / Loss', color: 'red' },
  DIRECT_SALE: { label: 'Direct Sale', color: 'purple' },
  ADJUSTMENT: { label: 'Adjustment', color: 'geekblue' },
};

const MANUAL_TYPE_OPTIONS = [
  { value: 'MANUAL_IN', label: 'Manual Stock IN', hint: 'Opening stock, owner metal, returned items. Posts: Dr Inventory / Cr Opening Balance Equity.' },
  { value: 'WRITE_OFF', label: 'Write-off / Loss OUT', hint: 'Loss, theft, audit shortage. Posts: Dr Stock Write-off (P&L) / Cr Inventory.' },
  { value: 'DIRECT_SALE', label: 'Direct Sale OUT', hint: 'Counter sale outside the smelter flow. Posts: Dr the chosen fund\'s cash/bank account / Cr Inventory, profit or loss booked, and credits that fund in Manage Company Funds.' },
  { value: 'ADJUSTMENT', label: 'Correction / Adjustment', hint: 'Fix a wrong entry either direction against equity — no P&L impact.' },
];

const STATUS_BADGE = {
  PENDING: { status: 'processing', text: 'Pending Approval' },
  COMPLETED: { status: 'success', text: 'Completed' },
  REJECTED: { status: 'error', text: 'Rejected' },
  CANCELLED: { status: 'default', text: 'Cancelled' },
};

const fmt = (v) => Number((v || 0).toFixed(2)).toLocaleString('en-IN');
const fmtWt = (v) => Number((v || 0).toFixed(4)).toLocaleString('en-IN');

export default function StockRegisterPage() {
  const [summary, setSummary] = useState(null);
  const [movements, setMovements] = useState([]);
  const [meta, setMeta] = useState({ total: 0, page: 1, limit: 25 });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ metalType: null, type: null, status: null, dateRange: null });
  const [page, setPage] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [cancellingId, setCancellingId] = useState(null);
  const [funds, setFunds] = useState([]);
  const [fundsLoaded, setFundsLoaded] = useState(false);
  const [denoms, setDenoms] = useState({});          // cash-drawer note breakdown
  const [denomOpen, setDenomOpen] = useState(false);
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 25 };
      if (filters.metalType) params.metalType = filters.metalType;
      if (filters.type) params.type = filters.type;
      if (filters.status) params.status = filters.status;
      if (filters.dateRange?.[0]) params.startDate = filters.dateRange[0].startOf('day').toISOString();
      if (filters.dateRange?.[1]) params.endDate = filters.dateRange[1].endOf('day').toISOString();

      const [sumRes, listRes] = await Promise.all([
        fetchStockSummary(),
        fetchStockMovements(params),
      ]);
      setSummary(sumRes.data);
      setMovements(listRes.data || []);
      setMeta(listRes.meta || { total: 0, page: 1, limit: 25 });
    } catch (err) {
      messageApi.error(err.response?.data?.message || 'Failed to load stock register');
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  useEffect(() => { load(); }, [load]);

  // Funds are only needed by the manual-movement modal (direct sale proceeds).
  const openModal = async () => {
    form.resetFields();
    setDenoms({});
    setModalOpen(true);
    if (fundsLoaded) return;
    try {
      const res = await fetchFunds();
      setFunds((res.data || []).filter((f) => f.isActive !== false));
      setFundsLoaded(true);
    } catch (err) {
      messageApi.error(err.response?.data?.message || 'Failed to load company funds');
    }
  };

  const fundById = (id) => funds.find((f) => f._id === id) || null;

  const handleCreate = async (values) => {
    const isSale = values.type === 'DIRECT_SALE';
    const saleFund = isSale ? fundById(values.saleProceedsFundId) : null;

    if (saleFund?.isIBCTCashFund) {
      const counted = denomTotal(denoms);
      if (Number(counted.toFixed(2)) !== Number((values.salePrice || 0).toFixed(2))) {
        messageApi.error(`Note breakdown (₹${fmt(counted)}) must equal the sale price (₹${fmt(values.salePrice)})`);
        return;
      }
    }

    setSaving(true);
    try {
      const res = await createStockMovement({
        type: values.type,
        direction: values.direction,
        metalType: values.metalType,
        grossWeight: values.grossWeight,
        netWeight: values.netWeight,
        amount: values.amount,
        salePrice: values.salePrice,
        saleProceedsMode: values.saleProceedsMode,
        saleProceedsFundId: isSale ? values.saleProceedsFundId : undefined,
        saleProceedsDenominations: saleFund?.isIBCTCashFund ? denoms : undefined,
        saleBankAccount: saleFund?.isCentralFund ? (values.saleBankAccount || '') : undefined,
        saleBankReference: saleFund?.isCentralFund ? (values.saleBankReference || '') : undefined,
        saleValueDate: saleFund?.isCentralFund && values.saleValueDate ? values.saleValueDate.toISOString() : undefined,
        narration: values.narration || '',
        movementDate: values.movementDate ? values.movementDate.toISOString() : undefined,
      });
      messageApi.success(res.message || 'Movement submitted');
      setModalOpen(false);
      form.resetFields();
      setDenoms({});
      load();
    } catch (err) {
      messageApi.error(err.response?.data?.message || 'Failed to submit movement');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = async (row) => {
    setCancellingId(row._id);
    try {
      await cancelStockMovement(row._id);
      messageApi.success(`Movement ${row.movementId} cancelled`);
      load();
    } catch (err) {
      messageApi.error(err.response?.data?.message || 'Failed to cancel');
    } finally {
      setCancellingId(null);
    }
  };

  const columns = [
    {
      title: 'Date', dataIndex: 'movementDate', width: 100,
      render: (d) => d ? dayjs(d).format('DD/MM/YYYY') : '—',
    },
    { title: 'Ref', dataIndex: 'movementId', width: 105, render: (v) => <Text strong style={{ fontFamily: 'monospace', fontSize: 12 }}>{v}</Text> },
    {
      title: 'Type', dataIndex: 'type', width: 175,
      render: (t) => {
        const m = MOVEMENT_TYPE_META[t] || { label: t, color: 'default' };
        return <Tag color={m.color}>{m.label}</Tag>;
      },
    },
    {
      title: 'Metal', dataIndex: 'metalType', width: 80,
      render: (m) => <Tag color={m === 'SILVER' ? 'default' : 'gold'}>{m === 'SILVER' ? 'Silver' : 'Gold'}</Tag>,
    },
    {
      title: 'In/Out', dataIndex: 'direction', width: 80,
      render: (d) => d === 'IN'
        ? <Tag color="green" icon={<ArrowDownOutlined />}>IN</Tag>
        : <Tag color="red" icon={<ArrowUpOutlined />}>OUT</Tag>,
    },
    {
      title: 'Gross Wt (gm)', dataIndex: 'grossWeight', align: 'right', width: 115,
      // Rows written before gross weight was captured fall back to net, matching
      // the backend summary aggregation.
      render: (v, r) => fmtWt(v || r.netWeight),
    },
    { title: 'Value (₹)', dataIndex: 'amount', align: 'right', width: 120, render: (v) => `₹${fmt(v)}` },
    {
      title: 'Sale Price', dataIndex: 'salePrice', align: 'right', width: 110,
      render: (v, r) => r.type === 'DIRECT_SALE' && v != null ? `₹${fmt(v)}` : <Text type="secondary">—</Text>,
    },
    {
      title: 'Proceeds Into', dataIndex: 'saleProceedsFund', width: 160,
      render: (f, r) => f
        ? <Tooltip title={`${r.saleProceedsMode === 'BANK' ? 'Bank' : 'Cash'} · GL ${f.linkedAccountCode || '—'}`}>
            <Tag color={r.saleProceedsMode === 'BANK' ? 'blue' : 'green'}>{f.name}</Tag>
          </Tooltip>
        : <Text type="secondary">—</Text>,
    },
    { title: 'Narration', dataIndex: 'narration', ellipsis: true },
    {
      title: 'Journal', dataIndex: 'journalEntry', width: 110,
      render: (je) => je?.entryId ? <Tag color="blue">{je.entryId}</Tag> : <Text type="secondary">—</Text>,
    },
    {
      title: 'Status', dataIndex: 'status', width: 140,
      render: (s) => {
        const b = STATUS_BADGE[s] || { status: 'default', text: s };
        return <Badge status={b.status} text={b.text} />;
      },
    },
    {
      title: 'Actions', key: 'actions', width: 100, fixed: 'right',
      render: (_, row) => row.status === 'PENDING' ? (
        <Popconfirm
          title="Withdraw this pending movement?"
          description="It leaves every approver's queue and nothing is posted."
          okText="Withdraw" okButtonProps={{ danger: true }}
          onConfirm={() => handleCancel(row)}
        >
          <Button size="small" danger loading={cancellingId === row._id}>Cancel</Button>
        </Popconfirm>
      ) : <Text type="secondary">—</Text>,
    },
  ];

  const metalCard = (metal, data, color) => (
    <Col xs={24} md={12} key={metal}>
      <Card size="small" title={<Tag color={color}>{metal === 'GOLD' ? 'Gold' : 'Silver'} Stock</Tag>}>
        <Row gutter={16}>
          <Col span={8}><Statistic title="In Stock (gross gm)" value={fmtWt(data?.netWeight)} valueStyle={{ fontSize: 18 }} /></Col>
          <Col span={8}><Statistic title="Book Value" value={fmt(data?.netAmount)} prefix="₹" valueStyle={{ fontSize: 18 }} /></Col>
          <Col span={8}>
            <Statistic title="Movements" value={data?.movements || 0} valueStyle={{ fontSize: 18 }} />
          </Col>
        </Row>
        <Row gutter={16} style={{ marginTop: 8 }}>
          <Col span={12}><Text type="secondary" style={{ fontSize: 12 }}>Total IN: {fmtWt(data?.inWeight)} gm · ₹{fmt(data?.inAmount)}</Text></Col>
          <Col span={12}><Text type="secondary" style={{ fontSize: 12 }}>Total OUT: {fmtWt(data?.outWeight)} gm · ₹{fmt(data?.outAmount)}</Text></Col>
        </Row>
      </Card>
    </Col>
  );

  if (loading && !summary) {
    return <Spin size="large" style={{ display: 'block', margin: '80px auto' }} />;
  }

  return (
    <div>
      {contextHolder}

      {/* Header */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col><Title level={5} style={{ margin: 0 }}>Stock Register</Title></Col>
        <Col>
          <Space>
            <Button type="primary" icon={<PlusOutlined />} onClick={openModal}>
              New Manual Movement
            </Button>
            <Tooltip title="Refresh"><Button icon={<ReloadOutlined />} onClick={load} loading={loading} /></Tooltip>
          </Space>
        </Col>
      </Row>

      {/* Pending banner */}
      {summary?.pendingCount > 0 && (
        <Alert
          style={{ marginBottom: 16 }} type="info" showIcon icon={<ClockCircleOutlined />}
          message={`${summary.pendingCount} manual movement(s) awaiting approval`}
          description="They appear below as Pending Approval and will post to stock and the books once approved (Workflow → My Approvals)."
        />
      )}

      {/* Summary */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        {metalCard('GOLD', summary?.summary?.GOLD, 'gold')}
        {metalCard('SILVER', summary?.summary?.SILVER, 'default')}
      </Row>

      {/* Filters */}
      <Card size="small" style={{ marginBottom: 12 }}>
        <Space wrap>
          <Select allowClear placeholder="Metal" style={{ width: 110 }} value={filters.metalType}
            onChange={(v) => { setPage(1); setFilters(f => ({ ...f, metalType: v || null })); }}
            options={[{ value: 'GOLD', label: 'Gold' }, { value: 'SILVER', label: 'Silver' }]} />
          <Select allowClear placeholder="Movement type" style={{ width: 200 }} value={filters.type}
            onChange={(v) => { setPage(1); setFilters(f => ({ ...f, type: v || null })); }}
            options={Object.entries(MOVEMENT_TYPE_META).map(([value, m]) => ({ value, label: m.label }))} />
          <Select allowClear placeholder="Status" style={{ width: 160 }} value={filters.status}
            onChange={(v) => { setPage(1); setFilters(f => ({ ...f, status: v || null })); }}
            options={Object.entries(STATUS_BADGE).map(([value, b]) => ({ value, label: b.text }))} />
          <RangePicker value={filters.dateRange} format="DD/MM/YYYY"
            onChange={(v) => { setPage(1); setFilters(f => ({ ...f, dateRange: v })); }} />
        </Space>
      </Card>

      {/* Movements */}
      <Card size="small">
        <DataTable persistId="accounting.stock-register.main" serverPaginated
          dataSource={movements.map((m) => ({ ...m, key: m._id }))}
          columns={columns}
          loading={loading}
          size="small"
          scroll={{ x: 1460 }}
          pagination={{
            current: meta.page, pageSize: meta.limit, total: meta.total,
            showTotal: (t) => `${t} movements`,
            onChange: (p) => setPage(p),
          }}
        />
      </Card>

      {/* Manual movement modal */}
      <Modal
        title="New Manual Stock Movement"
        open={modalOpen}
        onOk={() => form.submit()}
        onCancel={() => setModalOpen(false)}
        confirmLoading={saving}
        okText="Submit for Approval"
        width={560}
        destroyOnClose
      >
        <Alert
          type="warning" showIcon style={{ marginBottom: 12 }}
          message="Requires approval"
          description="Nothing touches stock or the books until this movement is approved in Workflow → My Approvals."
        />
        <Form form={form} layout="vertical" onFinish={handleCreate}
          initialValues={{ metalType: 'GOLD', direction: 'IN', saleProceedsMode: 'CASH', movementDate: dayjs() }}>
          <Form.Item name="type" label="Movement Type" rules={[{ required: true, message: 'Required' }]}>
            <Select
              placeholder="What happened?"
              options={MANUAL_TYPE_OPTIONS.map(o => ({ value: o.value, label: o.label }))}
            />
          </Form.Item>
          <Form.Item noStyle shouldUpdate={(p, c) => p.type !== c.type}>
            {({ getFieldValue }) => {
              const t = MANUAL_TYPE_OPTIONS.find(o => o.value === getFieldValue('type'));
              return t ? (
                <div style={{ marginTop: -12, marginBottom: 12 }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>{t.hint}</Text>
                </div>
              ) : null;
            }}
          </Form.Item>

          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="metalType" label="Metal" rules={[{ required: true }]}>
                <Radio.Group optionType="button" buttonStyle="solid"
                  options={[{ label: 'Gold', value: 'GOLD' }, { label: 'Silver', value: 'SILVER' }]} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item noStyle shouldUpdate={(p, c) => p.type !== c.type}>
                {({ getFieldValue }) => getFieldValue('type') === 'ADJUSTMENT' && (
                  <Form.Item name="direction" label="Direction" rules={[{ required: true }]}>
                    <Radio.Group optionType="button"
                      options={[{ label: 'Add (IN)', value: 'IN' }, { label: 'Remove (OUT)', value: 'OUT' }]} />
                  </Form.Item>
                )}
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col span={8}>
              <Form.Item name="grossWeight" label="Gross Wt (gm)" rules={[{ required: true, message: 'Required' }]}
                tooltip="The register reports gross weight — this is the figure that shows in the movement list and stock totals.">
                <InputNumber min={0.0001} precision={4} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="netWeight" label="Net Wt (gm)"
                tooltip="Optional. Leave blank for pure metal — it defaults to the gross weight.">
                <InputNumber min={0} precision={4} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="amount" label="Book Value (₹)" rules={[{ required: true, message: 'Required' }]}
                tooltip="The inventory value of this metal — for a sale this is the COST, not the price.">
                <InputNumber min={0.01} precision={2} style={{ width: '100%' }} prefix="₹" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item noStyle shouldUpdate={(p, c) => p.type !== c.type
            || p.saleProceedsMode !== c.saleProceedsMode
            || p.saleProceedsFundId !== c.saleProceedsFundId
            || p.salePrice !== c.salePrice}>
            {({ getFieldValue, setFieldsValue }) => {
              if (getFieldValue('type') !== 'DIRECT_SALE') return null;

              const mode = getFieldValue('saleProceedsMode');
              const price = Number(getFieldValue('salePrice')) || 0;
              const candidates = funds.filter((f) => fundProceedsMode(f.linkedAccountCode) === mode);
              const selectedFund = fundById(getFieldValue('saleProceedsFundId'));
              const counted = denomTotal(denoms);

              return (
                <>
                  <Row gutter={12}>
                    <Col span={12}>
                      <Form.Item name="salePrice" label="Sale Price (₹)" rules={[{ required: true, message: 'Required' }]}
                        tooltip="What the buyer actually paid. Difference vs book value posts as profit or loss.">
                        <InputNumber min={0.01} precision={2} style={{ width: '100%' }} prefix="₹" />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item name="saleProceedsMode" label="Proceeds Received In" rules={[{ required: true }]}>
                        <Radio.Group optionType="button"
                          options={[{ label: 'Cash', value: 'CASH' }, { label: 'Bank', value: 'BANK' }]}
                          onChange={() => { setFieldsValue({ saleProceedsFundId: undefined }); setDenoms({}); }} />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Form.Item
                    name="saleProceedsFundId"
                    label={`Credit Into Fund (${mode === 'BANK' ? 'bank' : 'cash'} funds)`}
                    rules={[{ required: true, message: 'Select the fund the money was received into' }]}
                    extra="The sale proceeds land in this fund (Manage Company Funds), and the journal debits its linked GL account."
                  >
                    <Select
                      placeholder={fundsLoaded ? `Select a ${mode === 'BANK' ? 'bank' : 'cash'} fund` : 'Loading funds…'}
                      loading={!fundsLoaded}
                      showSearch
                      optionFilterProp="label"
                      onChange={() => setDenoms({})}
                      options={candidates.map((f) => ({
                        value: f._id,
                        label: `${f.name} — ₹${fmt(f.balance)} · ${f.linkedAccountCode}`
                          + `${f.isCentralFund ? '  ★ HO Bank' : ''}${f.isIBCTCashFund ? '  ★ Cash Drawer' : ''}`,
                      }))}
                    />
                  </Form.Item>

                  {fundsLoaded && candidates.length === 0 && (
                    <Alert
                      type="error" showIcon style={{ marginTop: -8, marginBottom: 12 }}
                      message={`No ${mode === 'BANK' ? 'bank' : 'cash'} fund available`}
                      description={`Go to Accounting → Manage Company Funds and link a fund to a ${mode === 'BANK' ? 'bank (11xx)' : 'cash (10xx)'} GL account first.`}
                    />
                  )}

                  {/* Cash-drawer fund — the note breakdown must equal the sale price */}
                  {selectedFund?.isIBCTCashFund && (
                    <div style={{ marginTop: -4, marginBottom: 12, padding: 8, background: 'rgba(0,0,0,0.02)', borderRadius: 6 }}>
                      <Space wrap>
                        <WalletOutlined />
                        <Text style={{ fontSize: 12 }}>
                          Cash drawer — note breakdown required. Counted: <b>₹{fmt(counted)}</b> / needed ₹{fmt(price)}
                        </Text>
                        <Button size="small" onClick={() => setDenomOpen(true)}>Edit notes</Button>
                        {counted !== price && <Tag color="error" style={{ fontSize: 11 }}>Does not match</Tag>}
                      </Space>
                    </div>
                  )}

                  {/* HO-bank fund — reconciliation details for the Central Fund ledger */}
                  {selectedFund?.isCentralFund && (
                    <div style={{ marginTop: -4, marginBottom: 12 }}>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        <BankOutlined /> Also records a Bank Credit on the treasury Central Fund ledger.
                      </Text>
                      <Row gutter={8} style={{ marginTop: 6 }}>
                        <Col span={8}><Form.Item name="saleBankAccount" noStyle><Input size="small" placeholder="Bank account" /></Form.Item></Col>
                        <Col span={8}><Form.Item name="saleBankReference" noStyle><Input size="small" placeholder="Bank reference / UTR" /></Form.Item></Col>
                        <Col span={8}><Form.Item name="saleValueDate" noStyle><DatePicker size="small" style={{ width: '100%' }} placeholder="Value date" format="DD/MM/YYYY" /></Form.Item></Col>
                      </Row>
                    </div>
                  )}
                </>
              );
            }}
          </Form.Item>

          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="movementDate" label="Movement Date">
                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="narration" label="Narration / Reason" rules={[{ required: true, message: 'Explain why — approvers see this' }]}>
            <Input.TextArea rows={2} placeholder="e.g. Opening silver stock counted at HO vault" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Note breakdown for a cash-drawer proceeds fund */}
      <Modal
        title="Cash Drawer — Note Breakdown"
        open={denomOpen}
        onCancel={() => setDenomOpen(false)}
        onOk={() => setDenomOpen(false)}
        okText="Done"
        width={520}
        destroyOnClose
      >
        <DenominationCountForm counts={denoms} onChange={setDenoms} />
        <Row justify="space-between" style={{ marginTop: 10 }}>
          <Text>Counted: <b>₹{fmt(denomTotal(denoms))}</b></Text>
          <Text>Sale price: <b>₹{fmt(form.getFieldValue('salePrice'))}</b></Text>
        </Row>
      </Modal>
    </div>
  );
}
